import type { ReactNode } from "react";
import styled from "styled-components";

import { ChevronIcon, ChevronUpIcon, InfoIcon } from "../../icons/glyphs";
import { TooltipIconButton } from "../IconButton";
import { ElevatedSurface } from "../Surface";
import { Text, defaultTextVariants } from "../Text";
import {
  AnnotationPanelHeader,
  AnnotationPanelHeaderActions,
  AnnotationPanelHeaderTitle,
} from "./AnnotationPanelLayout";
import { annotationStudioText } from "./messages";
import type { AnnotationStudioLocale } from "./types";

const ReviewLabel = styled(Text)`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.color.text.default};
  white-space: nowrap;
`;

const ReviewNotice = styled(ElevatedSurface)`
  box-sizing: border-box;
  display: grid;
  width: 100%;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 0.5rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid ${({ theme }) => theme.color.border.default};

  & > svg {
    margin-top: 0.1rem;
    color: ${({ theme }) => theme.color.status.info};
  }
`;

const ReviewNoticeCopy = styled.div`
  display: grid;
  min-width: 0;
  gap: 0.1rem;
`;

const ReviewNoticeDescription = styled(Text)`
  color: ${({ theme }) => theme.color.text.subtle};
`;

export interface AnnotationPanelHeaderContentProps {
  readonly locale: AnnotationStudioLocale;
  readonly toolsVisible: boolean;
  readonly authoring: boolean;
  readonly annotationActions?: ReactNode;
  readonly compact?: boolean;
  readonly onCompactChange?: (compact: boolean) => void;
  readonly toolsContentId?: string;
}

export function AnnotationPanelHeaderContent({
  locale,
  toolsVisible,
  authoring,
  annotationActions,
  compact = false,
  onCompactChange,
  toolsContentId,
}: AnnotationPanelHeaderContentProps) {
  return (
    <>
      <AnnotationPanelHeader data-annotation-panel-header="">
        <AnnotationPanelHeaderTitle>
          <Text variant={defaultTextVariants["body-bold"]}>
            {annotationStudioText("annotationStatus", locale)}
          </Text>
        </AnnotationPanelHeaderTitle>
        <AnnotationPanelHeaderActions data-annotation-panel-header-actions="">
          {toolsVisible ? annotationActions : null}
          {toolsVisible && onCompactChange ? (
            <TooltipIconButton
              size="xs"
              variant="ghost"
              tooltip={annotationStudioText(
                compact ? "expandAnnotationSlots" : "compactAnnotationSlots",
                locale,
              )}
              aria-expanded={!compact}
              aria-controls={toolsContentId}
              onClick={() => onCompactChange(!compact)}
              onKeyDown={(event) => {
                if (event.key === " ") event.stopPropagation();
              }}
            >
              {compact ? <ChevronIcon /> : <ChevronUpIcon />}
            </TooltipIconButton>
          ) : null}
          {!authoring ? (
            <ReviewLabel variant={defaultTextVariants["caption-bold"]} data-annotation-read-only="">
              {annotationStudioText("reviewOnly", locale)}
            </ReviewLabel>
          ) : null}
        </AnnotationPanelHeaderActions>
      </AnnotationPanelHeader>
      {!authoring ? (
        <ReviewNotice
          elevation={0}
          background="surface"
          hoverElevation={false}
          data-annotation-read-only-notice=""
        >
          <InfoIcon aria-hidden="true" />
          <ReviewNoticeCopy>
            <Text variant={defaultTextVariants["caption-bold"]}>
              {annotationStudioText("reviewEditingUnavailable", locale)}
            </Text>
            <ReviewNoticeDescription variant={defaultTextVariants["caption-normal"]}>
              {annotationStudioText("reviewOnlyDescription", locale)}
            </ReviewNoticeDescription>
          </ReviewNoticeCopy>
        </ReviewNotice>
      ) : null}
    </>
  );
}
